import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Select,
  SelectItem,
  Skeleton,
  Card,
  CardBody,
} from '@nextui-org/react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  getDashboardFobVsExporters,
  getUniqueVarietiesWithCount,
} from 'common/src/api/settlements';
import { COLORS } from 'common/src/utils/colors';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import ChartDownloadButton from '../../../../common/src/components/charts/ChartDownloadButton';

interface BoxPlotData {
  own:
    | {
        value: number;
        kilos: number;
      }[]
    | null;
  others: {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
  };
}

interface WeekData {
  [caliber: string]: BoxPlotData;
}

interface VarietyData {
  [week: string]: WeekData;
}

interface ComparativeData {
  [variety: string]: VarietyData;
}

type FobVsExportersMethodology = 'by_exporter' | 'by_volume';

const CALIBERS = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
const DEFAULT_FOB_VS_EXPORTERS_METHODOLOGY: FobVsExportersMethodology =
  'by_exporter';

const CIFEvolutionWidget = () => {
  const { t } = useTranslation('dashboard');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { userProfile, session } = useUserStore();
  const [data, setData] = useState<ComparativeData>({});
  const [loading, setLoading] = useState(true);
  const [varietiesLoading, setVarietiesLoading] = useState(true);
  const [selectedCaliber, setSelectedCaliber] = useState<string>('2J');
  const [priceType, setPriceType] = useState<'FOB' | 'CIF'>('FOB');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [weeks, setWeeks] = useState<string[]>([]);
  const [selectedVariety, setSelectedVariety] = useState<string>('');
  const [varieties, setVarieties] = useState<
    Array<{ variety: string; count: number }>
  >([]);
  const [selectedMethodology, setSelectedMethodology] =
    useState<FobVsExportersMethodology>(
      DEFAULT_FOB_VS_EXPORTERS_METHODOLOGY
    );

  // Fetch varieties first
  useEffect(() => {
    const fetchVarieties = async () => {
      try {
        setVarietiesLoading(true);
        setSelectedVariety('');

        // Skip if not cherry
        if (selectedSpecies?.id !== 2) {
          setVarietiesLoading(false);
          return;
        }

        const varietiesData = await getUniqueVarietiesWithCount(
          session,
          selectedSeason,
          selectedSpecies?.id
        );

        if (userProfile?.exporter?.hasDemo) {
          setVarieties([{ variety: 'REGINA', count: 100 }]);
          setSelectedVariety('REGINA');
          setSelectedCaliber('4J');
        } else if (varietiesData.length > 0) {
          setVarieties(varietiesData);
          setSelectedVariety(varietiesData[0].variety);
        }
      } catch (error) {
        console.error('Error fetching varieties:', error);
      } finally {
        setVarietiesLoading(false);
      }
    };

    if (session) {
      fetchVarieties();
    }
  }, [selectedSeason, selectedSpecies?.id]);

  // Fetch data for selected variety
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedVariety) {
        setLoading(false);
        return;
      }

      if (
        !selectedSpecies?.id ||
        !selectedSeason ||
        !userProfile?.exporter_id
      ) {
        setLoading(false);
        return;
      }

      // Only available for cherry
      if (selectedSpecies?.id !== 2) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const responseData = await getDashboardFobVsExporters(
          userProfile.exporter_id,
          selectedSeason,
          selectedSpecies.id,
          selectedVariety,
          selectedMethodology,
          priceType.toLowerCase() as 'fob' | 'cif',
          'eta',
          'all'
        );

        if (!responseData) {
          throw new Error('No response received from server');
        }

        setData(responseData);

        // Get unique weeks from all varieties
        const allWeeks = new Set<string>();
        Object.values(responseData).forEach((varietyData) => {
          Object.keys(varietyData || {}).forEach((week) => {
            allWeeks.add(week);
          });
        });

        const sortedWeeks = Array.from(allWeeks).sort((a, b) => {
          const weekA = parseInt(a.split('-W')[1]);
          const weekB = parseInt(b.split('-W')[1]);

          if (weekA >= 27 && weekB < 27) return -1;
          if (weekA < 27 && weekB >= 27) return 1;

          return weekA - weekB;
        });

        setWeeks(sortedWeeks);
      } catch (error) {
        console.error('Error fetching CIF/FOB evolution data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedSpecies?.id,
    selectedSeason,
    userProfile?.exporter_id,
    priceType,
    selectedVariety,
    selectedMethodology,
  ]);

  // Calculate weighted average FOB
  const calculateWeightedOwnValue = (
    ownData:
      | {
          value: number;
          kilos: number;
        }[]
      | null
  ) => {
    if (!ownData?.length) return null;
    const totalKilos = ownData.reduce(
      (sum, item) => sum + (item.kilos || 0),
      0
    );
    if (totalKilos === 0) return null;
    const weightedSum = ownData.reduce(
      (sum, item) => sum + item.value * (item.kilos || 0),
      0
    );
    return weightedSum / totalKilos;
  };

  // Prepare chart options with translations
  const chartOptions: ApexOptions = useMemo(() => {
    const isDark = document.documentElement.classList.contains('dark');
    const tooltipBg = isDark ? '#1a1a2e' : 'white';
    const tooltipBorder = isDark ? '#2a2a3e' : '#e3e3e3';
    const tooltipColor = isDark ? '#ECEDEE' : 'inherit';

    return {
      chart: {
        type: 'boxPlot',
        height: 350,
        foreColor: isDark ? '#A1A1AA' : '#71717A',
        toolbar: {
          show: false,
        },
      },
      colors: [COLORS.cherry, COLORS.lightGreen],
      stroke: {
        colors: [isDark ? '#ffffff' : '#000000'],
      },
      plotOptions: {
        boxPlot: {
          colors: {
            upper: COLORS.cherry,
            lower: COLORS.cherry,
          },
        },
      },
      xaxis: {
        type: 'category',
        title: {
          text: t('Semana'),
        },
        labels: {
          rotate: -45,
          style: {
            fontSize: '12px',
          },
        },
      },
      yaxis: {
        title: {
          text: `${priceType} USD/kg`,
          style: {
            fontSize: '14px',
            fontWeight: 500,
          },
        },
        labels: {
          formatter: (value) => `$${value ? value.toFixed(2) : 0}`,
        },
      },
      grid: {
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#f1f1f1',
        strokeDashArray: 3,
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ dataPointIndex, w }) {
          const boxData = w.globals.initialSeries[0]?.data[dataPointIndex];
          const scatterData = w.globals.initialSeries[1]?.data[dataPointIndex];

          const weekLabel = boxData?.x || scatterData?.x || '';
          const [min, q1, median, q3, max] = boxData?.y || [];
          const ownValue = scatterData?.y;

          const hasBoxData = min != null;
          const hasOwnData = ownValue != null;

          let html = `<div style="padding: 10px; background: ${tooltipBg}; border: 1px solid ${tooltipBorder}; color: ${tooltipColor}; border-radius: 6px; min-width: 180px;">`;
          html += `<div style="font-weight: 600; margin-bottom: 6px; font-size: 13px;">${t('Semana')} W${weekLabel}</div>`;

          if (hasBoxData) {
            html += `<div style="margin-bottom: 4px; padding-bottom: 4px; border-bottom: 1px solid ${tooltipBorder};">`;
            html += `<div style="font-weight: 600; color: ${COLORS.cherry}; margin-bottom: 2px;">${w.globals.seriesNames[0]}</div>`;
            html += `<div style="font-size: 12px;">${t('Mínimo')}: <strong>$${min?.toFixed(2)}/kg</strong></div>`;
            html += `<div style="font-size: 12px;">Q1: <strong>$${q1?.toFixed(2)}/kg</strong></div>`;
            html += `<div style="font-size: 12px;">${t('Mediana')}: <strong>$${median?.toFixed(2)}/kg</strong></div>`;
            html += `<div style="font-size: 12px;">Q3: <strong>$${q3?.toFixed(2)}/kg</strong></div>`;
            html += `<div style="font-size: 12px;">${t('Máximo')}: <strong>$${max?.toFixed(2)}/kg</strong></div>`;
            html += `</div>`;
          }

          if (hasOwnData) {
            html += `<div style="margin-top: 4px;">`;
            html += `<div style="font-weight: 600; color: ${COLORS.lightGreen}; margin-bottom: 2px;">${w.globals.seriesNames[1]}</div>`;
            html += `<div style="font-size: 12px;">${t('Valor')}: <strong>$${ownValue?.toFixed(2)}/kg</strong></div>`;
            html += `</div>`;
          }

          if (!hasBoxData && !hasOwnData) {
            html += `<div style="font-size: 12px; color: #999;">${t('Sin datos')}</div>`;
          }

          html += `</div>`;
          return html;
        },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
      },
    };
  }, [priceType, t]);

  // Prepare chart data - aggregate all varieties
  const chartData = useMemo(() => {
    if (!data || weeks.length === 0) {
      return [];
    }

    const boxPlotSeries = {
      name: t('INDUSTRIA'),
      type: 'boxPlot',
      data: weeks.map((week) => {
        // Aggregate data across all varieties for this week and caliber
        let minValues: number[] = [];
        let maxValues: number[] = [];
        let q1Values: number[] = [];
        let medianValues: number[] = [];
        let q3Values: number[] = [];

        Object.values(data).forEach((varietyData) => {
          const weekData = varietyData?.[week]?.[selectedCaliber];
          if (weekData?.others) {
            minValues.push(weekData.others.min);
            maxValues.push(weekData.others.max);
            q1Values.push(weekData.others.q1);
            medianValues.push(weekData.others.median);
            q3Values.push(weekData.others.q3);
          }
        });

        if (minValues.length === 0) {
          return {
            x: week.split('-W')[1],
            y: [null, null, null, null, null],
          };
        }

        // Calculate average statistics
        const avgMin = minValues.reduce((a, b) => a + b, 0) / minValues.length;
        const avgQ1 = q1Values.reduce((a, b) => a + b, 0) / q1Values.length;
        const avgMedian =
          medianValues.reduce((a, b) => a + b, 0) / medianValues.length;
        const avgQ3 = q3Values.reduce((a, b) => a + b, 0) / q3Values.length;
        const avgMax = maxValues.reduce((a, b) => a + b, 0) / maxValues.length;

        return {
          x: week.split('-W')[1],
          y: [avgMin, avgQ1, avgMedian, avgQ3, avgMax],
        };
      }),
    };

    // Add scatter series for own data
    const scatterSeries = {
      name: t('PROPIO'),
      type: 'scatter',
      data: weeks.map((week) => {
        // Aggregate own data across all varieties
        let totalValue = 0;
        let totalKilos = 0;

        Object.values(data).forEach((varietyData) => {
          const weekData = varietyData?.[week]?.[selectedCaliber];
          if (weekData?.own) {
            weekData.own.forEach((ownItem) => {
              totalValue += ownItem.value * (ownItem.kilos || 0);
              totalKilos += ownItem.kilos || 0;
            });
          }
        });

        const avgValue = totalKilos > 0 ? totalValue / totalKilos : null;

        return {
          x: week.split('-W')[1],
          y: avgValue,
        };
      }),
    };

    return [boxPlotSeries, scatterSeries];
  }, [data, weeks, selectedCaliber, t]);

  const methodologyLabel =
    selectedMethodology === 'by_exporter'
      ? t('Metodología: Exportadora (igual peso por exportadora)')
      : t('Metodología: Volumen (ponderado por kilos)');

  if (loading || varietiesLoading) {
    return (
      <Card>
        <CardBody className='p-4 space-y-4'>
          <Skeleton className='h-8 w-1/3 rounded-lg' />
          <Skeleton className='h-10 w-full rounded-lg' />
          <Skeleton className='h-64 w-full rounded-lg' />
        </CardBody>
      </Card>
    );
  }

  if (selectedSpecies?.id !== 2) {
    return (
      <Card>
        <CardBody className='p-4'>
          {/* Título y subtítulo */}
          <div className='mb-3'>
            <h3 className='text-lg font-bold'>
              {t('Evolutivo')} {priceType} {t('vs Industria')}
            </h3>
            <span className='text-sm text-default-400'>
              {selectedSeason} - {methodologyLabel}
            </span>
          </div>

          {/* Filtros en una fila separada */}
          <div className='flex flex-wrap gap-2 mb-4'>
            <Select
              label={t('Variedad')}
              selectedKeys={selectedVariety ? [selectedVariety] : []}
              onChange={(e) => setSelectedVariety(e.target.value)}
              className='w-40'
              size='sm'
              aria-label='Selecciona variedad'
              isDisabled={true}
            >
              {varieties.map((v) => (
                <SelectItem key={v.variety} value={v.variety}>
                  {v.variety}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Tipo')}
              selectedKeys={priceType ? [priceType] : []}
              onChange={(e) => setPriceType(e.target.value as 'FOB' | 'CIF')}
              className='w-32'
              size='sm'
              aria-label='Selecciona tipo de precio'
              isDisabled={true}
            >
              <SelectItem key='FOB' value='FOB'>
                FOB
              </SelectItem>
              <SelectItem key='CIF' value='CIF'>
                CIF
              </SelectItem>
            </Select>

            <Select
              label={t('Calibre')}
              selectedKeys={selectedCaliber ? [selectedCaliber] : []}
              onChange={(e) => setSelectedCaliber(e.target.value)}
              className='w-32'
              size='sm'
              aria-label='Selecciona calibre'
              isDisabled={true}
            >
              {CALIBERS.map((caliber) => (
                <SelectItem key={caliber} value={caliber}>
                  {caliber}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Metodología')}
              selectedKeys={[selectedMethodology]}
              onChange={(e) =>
                setSelectedMethodology(
                  e.target.value as FobVsExportersMethodology
                )
              }
              className='w-52'
              size='sm'
              aria-label='Selecciona metodología'
              isDisabled={true}
            >
              <SelectItem key='by_exporter' value='by_exporter'>
                {t('Exportadora')}
              </SelectItem>
              <SelectItem key='by_volume' value='by_volume'>
                {t('Volumen')}
              </SelectItem>
            </Select>
          </div>

          <div className='text-center p-8 min-h-[300px] flex items-center justify-center'>
            <p className='text-default-400'>
              {t('Esta visualización solo está disponible para cerezas')}
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (weeks.length === 0) {
    return (
      <Card>
        <CardBody className='p-4'>
          {/* Título y subtítulo */}
          <div className='mb-3'>
            <h3 className='text-lg font-bold'>
              {t('Evolutivo')} {priceType} {t('vs Industria')}
            </h3>
            <span className='text-sm text-default-400'>
              {selectedSeason} - {methodologyLabel}
            </span>
          </div>

          {/* Filtros en una fila separada */}
          <div className='flex flex-wrap gap-2 mb-4'>
            <Select
              label={t('Variedad')}
              selectedKeys={selectedVariety ? [selectedVariety] : []}
              onChange={(e) => setSelectedVariety(e.target.value)}
              className='w-40'
              size='sm'
              aria-label='Selecciona variedad'
              isDisabled={
                varietiesLoading ||
                varieties.length === 0 ||
                !!userProfile?.exporter?.hasDemo
              }
            >
              {varieties.map((v) => (
                <SelectItem key={v.variety} value={v.variety}>
                  {v.variety}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Tipo')}
              selectedKeys={priceType ? [priceType] : []}
              onChange={(e) => setPriceType(e.target.value as 'FOB' | 'CIF')}
              className='w-32'
              size='sm'
              aria-label='Selecciona tipo de precio'
              isDisabled={!!userProfile?.exporter?.hasDemo}
            >
              <SelectItem key='FOB' value='FOB'>
                FOB
              </SelectItem>
              <SelectItem key='CIF' value='CIF'>
                CIF
              </SelectItem>
            </Select>

            <Select
              label={t('Calibre')}
              selectedKeys={selectedCaliber ? [selectedCaliber] : []}
              onChange={(e) => setSelectedCaliber(e.target.value)}
              className='w-32'
              size='sm'
              aria-label='Selecciona calibre'
              isDisabled={!!userProfile?.exporter?.hasDemo}
            >
              {CALIBERS.map((caliber) => (
                <SelectItem key={caliber} value={caliber}>
                  {caliber}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Metodología')}
              selectedKeys={[selectedMethodology]}
              onChange={(e) =>
                setSelectedMethodology(
                  e.target.value as FobVsExportersMethodology
                )
              }
              className='w-52'
              size='sm'
              aria-label='Selecciona metodología'
              isDisabled={!!userProfile?.exporter?.hasDemo}
            >
              <SelectItem key='by_exporter' value='by_exporter'>
                {t('Exportadora')}
              </SelectItem>
              <SelectItem key='by_volume' value='by_volume'>
                {t('Volumen')}
              </SelectItem>
            </Select>
          </div>

          <div className='text-center p-8 min-h-[300px] flex items-center justify-center'>
            <p className='text-default-400'>
              {t('No hay datos disponibles para la temporada seleccionada')}
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className='p-4'>
        {/* Título y subtítulo */}
        <div className='mb-3'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-bold'>
              {t('Evolutivo')} {priceType} {t('vs Industria')}
            </h3>
            <ChartDownloadButton
              chartRef={chartContainerRef}
              title={`${t('Evolutivo')} ${priceType} ${t('vs Industria')}`}
              subtitle={`${selectedSeason} - ${methodologyLabel}`}
            />
          </div>
          <span className='text-sm text-default-400'>
            {selectedSeason} - {methodologyLabel}
          </span>
        </div>

        {/* Filtros en una fila separada */}
        <div className='flex flex-wrap gap-2 mb-4'>
          <Select
            label={t('Variedad')}
            selectedKeys={selectedVariety ? [selectedVariety] : []}
            onChange={(e) => setSelectedVariety(e.target.value)}
            className='w-40'
            size='sm'
            aria-label='Selecciona variedad'
            isDisabled={
              varietiesLoading ||
              varieties.length === 0 ||
              !!userProfile?.exporter?.hasDemo
            }
          >
            {varieties.map((v) => (
              <SelectItem key={v.variety} value={v.variety}>
                {v.variety}
              </SelectItem>
            ))}
          </Select>

          <Select
            label={t('Tipo')}
            selectedKeys={priceType ? [priceType] : []}
            onChange={(e) => setPriceType(e.target.value as 'FOB' | 'CIF')}
            className='w-32'
            size='sm'
            aria-label='Selecciona tipo de precio'
            isDisabled={!!userProfile?.exporter?.hasDemo}
          >
            <SelectItem key='FOB' value='FOB'>
              FOB
            </SelectItem>
            <SelectItem key='CIF' value='CIF'>
              CIF
            </SelectItem>
          </Select>

          <Select
            label={t('Calibre')}
            selectedKeys={selectedCaliber ? [selectedCaliber] : []}
            onChange={(e) => setSelectedCaliber(e.target.value)}
            className='w-32'
            size='sm'
            aria-label='Selecciona calibre'
            isDisabled={!!userProfile?.exporter?.hasDemo}
          >
            {CALIBERS.map((caliber) => (
              <SelectItem key={caliber} value={caliber}>
                {caliber}
              </SelectItem>
            ))}
          </Select>

          <Select
            label={t('Metodología')}
            selectedKeys={[selectedMethodology]}
            onChange={(e) =>
              setSelectedMethodology(
                e.target.value as FobVsExportersMethodology
              )
            }
            className='w-52'
            size='sm'
            aria-label='Selecciona metodología'
            isDisabled={!!userProfile?.exporter?.hasDemo}
          >
            <SelectItem key='by_exporter' value='by_exporter'>
              {t('Exportadora')}
            </SelectItem>
            <SelectItem key='by_volume' value='by_volume'>
              {t('Volumen')}
            </SelectItem>
          </Select>
        </div>

        {userProfile?.exporter?.hasDemo && (
          <div className='p-4 mb-4 border border-blue-200 bg-blue-50 dark:bg-blue-950 rounded-lg'>
            <p className='text-blue-600 text-sm font-medium'>
              {t(
                'Estás viendo una versión de prueba. Contrata CherryMarket para ver los datos completos.'
              )}
            </p>
          </div>
        )}

        <div ref={chartContainerRef}>
          <ReactApexChart
            key={`${selectedCaliber}-${priceType}-${selectedVariety}-${selectedMethodology}`}
            options={chartOptions}
            series={chartData}
            type='boxPlot'
            height={350}
          />
        </div>
      </CardBody>
    </Card>
  );
};

export default CIFEvolutionWidget;
